import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Timeline as TimelineIcon,
  Star as StarIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
} from '@mui/icons-material';
import axios from 'axios';

const StatCard = ({ title, value, icon, color }) => (
  <Paper
    sx={{
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      height: 140,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -10,
        right: -10,
        opacity: 0.2,
        transform: 'rotate(30deg)',
      }}
    >
      {icon}
    </Box>
    <Typography component="h2" variant="h6" color="primary" gutterBottom>
      {title}
    </Typography>
    <Typography component="p" variant="h4">
      {value}
    </Typography>
  </Paper>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    totalUsers: 0,
    totalEvents: 0,
    totalBonuses: 0,
    dbConnections: {
      active_connections: 0,
      waiting_requests: 0,
      avg_wait_time: 0,
      max_wait_time: 0,
    },
    systemResources: {
      cpu_percent: 0,
      memory_percent: 0,
      memory_available_gb: 0,
      disk_usage_percent: 0,
      active_processes: 0,
    },
    backgroundJobResources: {
      cpu_percent: 0,
      memory_percent: 0,
      memory_available_gb: 0,
      memory_total_gb: 0,
      disk_read_mb: 0,
      disk_write_mb: 0,
      throttling_active: false,
      max_cpu_threshold: 50,
      max_memory_threshold: 70,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [clientsRes, usersRes, generationsRes, bonusesRes, dbConnectionsRes, systemResourcesRes, backgroundJobResourcesRes] = await Promise.all([
          axios.get('/admin/stats/clients'),
          axios.get('/admin/stats/users'),
          axios.get('/admin/stats/generations'),
          axios.get('/admin/stats/bonuses'),
          axios.get('/admin/stats/db-connections'),
          axios.get('/admin/stats/system-resources'),
          axios.get('/admin/stats/background-job-resources'),
        ]);

        setStats({
          totalClients: clientsRes.data.total || 0,
          totalUsers: usersRes.data.total || 0,
          totalEvents: generationsRes.data.total || 0,
          totalBonuses: bonusesRes.data.total || 0,
          dbConnections: dbConnectionsRes.data || {
            active_connections: 0,
            waiting_requests: 0,
            avg_wait_time: 0,
            max_wait_time: 0,
          },
          systemResources: systemResourcesRes.data || {
            cpu_percent: 0,
            memory_percent: 0,
            memory_available_gb: 0,
            disk_usage_percent: 0,
            active_processes: 0,
          },
          backgroundJobResources: backgroundJobResourcesRes.data || {
            cpu_percent: 0,
            memory_percent: 0,
            memory_available_gb: 0,
            memory_total_gb: 0,
            disk_read_mb: 0,
            disk_write_mb: 0,
            throttling_active: false,
            max_cpu_threshold: 50,
            max_memory_threshold: 70,
          },
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load statistics');
        setLoading(false);
      }
    };

    fetchStats();
    
    // Refresh stats every 10 seconds
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Clients"
            value={stats.totalClients}
            icon={<PeopleIcon sx={{ fontSize: 60, color: 'primary.main' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<PeopleIcon sx={{ fontSize: 60, color: 'success.main' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Events"
            value={stats.totalEvents}
            icon={<TimelineIcon sx={{ fontSize: 60, color: 'warning.main' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bonuses"
            value={stats.totalBonuses}
            icon={<StarIcon sx={{ fontSize: 60, color: 'info.main' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: 200,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                opacity: 0.2,
                transform: 'rotate(30deg)',
              }}
            >
              <StorageIcon sx={{ fontSize: 60, color: 'secondary.main' }} />
            </Box>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              DB Connections
            </Typography>
            <Typography component="p" variant="h4" gutterBottom>
              {stats.dbConnections.active_connections}
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              Waiting: {stats.dbConnections.waiting_requests}
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              Avg Wait: {stats.dbConnections.avg_wait_time.toFixed(3)}s
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              Max Wait: {stats.dbConnections.max_wait_time.toFixed(3)}s
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: 200,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                opacity: 0.2,
                transform: 'rotate(30deg)',
              }}
            >
              <MemoryIcon sx={{ fontSize: 60, color: 'warning.main' }} />
            </Box>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              System Resources
            </Typography>
            <Typography component="p" variant="h4" gutterBottom>
              {stats.systemResources.cpu_percent.toFixed(1)}%
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              CPU Usage
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              Memory: {stats.systemResources.memory_percent.toFixed(1)}%
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              Available: {stats.systemResources.memory_available_gb.toFixed(1)}GB
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: 200,
              position: 'relative',
              overflow: 'hidden',
              border: stats.backgroundJobResources.throttling_active ? '2px solid #ff9800' : 'none',
              bgcolor: stats.backgroundJobResources.throttling_active ? 'rgba(255, 152, 0, 0.1)' : 'inherit',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                opacity: 0.2,
                transform: 'rotate(30deg)',
              }}
            >
              <TimelineIcon sx={{ fontSize: 60, color: stats.backgroundJobResources.throttling_active ? 'warning.main' : 'info.main' }} />
            </Box>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              Background Jobs
            </Typography>
            <Typography component="p" variant="h4" gutterBottom>
              {stats.backgroundJobResources.throttling_active ? 'THROTTLED' : 'NORMAL'}
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              CPU: {stats.backgroundJobResources.cpu_percent.toFixed(1)}% / {stats.backgroundJobResources.max_cpu_threshold}%
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              Memory: {stats.backgroundJobResources.memory_percent.toFixed(1)}% / {stats.backgroundJobResources.max_memory_threshold}%
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              Available: {stats.backgroundJobResources.memory_available_gb.toFixed(1)}GB
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard; 